<template>
    <div class="select-container">
        <div class="select-item">
            <div class="select-item-header">
                <span class="label">All {{ label }}</span>
                <a
                    v-if="highlightedAvailableItems.length"
                    href="#"
                    :data-test-id="`${testId}-available-items-clear-highlight-button`"
                    @click.prevent="clearAvailableHighlight"
                    >Deselect All</a
                >
            </div>
            <Listbox
                v-model="highlightedAvailableItems"
                :options="availableItems"
                :optionLabel="labelKey"
                :optionValue="valueKey"
                class="available-items"
                emptyMessage="No Items Available"
                scrollHeight="260px"
                fluid
                multiple
                :filter="true"
                filter-match-mode="contains"
                :data-key="valueKey"
                :pt="listBoxPassThrough"
                :virtualScrollerOptions="virtualScrollerOptions"
                @item-dblclick="handleSelectItemDoubleClick"
            >
                <template #option="{ option, selected }">
                    <div
                        class="select-option"
                        :data-test-id="`${testId}-multiselect-available-option-${option[valueKey]}`"
                    >
                        <span class="select-option-label">
                            {{ option[labelKey] }}
                            <slot
                                name="option-suffix"
                                :option="option"
                            />
                        </span>

                        <button
                            v-if="selected"
                            type="button"
                            class="option-add-button"
                            :data-test-id="`${testId}-multiselect-available-option-${option[valueKey]}-select-button`"
                            @click.prevent="selectSingleItem(option)"
                        >
                            <img
                                src="/images/icon-plus.svg"
                                alt="Select"
                            />
                        </button>
                    </div>
                </template>
            </Listbox>
        </div>
        <div class="select-divider">
            <div>
                Move<br />Selected (<span :data-test-id="`${testId}-bulk-select-button-label-count`">{{
                    highlightedAvailableItems.length
                }}</span
                >)
            </div>
            <button
                type="button"
                :data-test-id="`${testId}-available-items-bulk-select-button`"
                :disabled="!highlightedAvailableItems.length"
                @click.prevent="selectHighlightedItems"
            >
                <img
                    src="/images/icon-right-arrow.svg"
                    alt="Select"
                />
            </button>
        </div>
        <div class="select-item">
            <div class="select-item-header">
                <span class="label"
                    >Selected {{ label }}
                    <span v-if="selectedItems.length"
                        >(<span :data-test-id="`${testId}-selected-items-label-count`">{{ selectedValues.length }}</span
                        >)</span
                    ></span
                >
                <a
                    v-if="selectedItems.length"
                    href="#"
                    :data-test-id="`${testId}-selected-items-clear-all-button`"
                    @click.prevent="unselectAll"
                    >Clear All</a
                >
            </div>

            <Listbox
                v-model="highlightedSelectedItem"
                :options="selectedItems"
                :optionLabel="labelKey"
                :optionValue="valueKey"
                :highlightOnSelect="false"
                :invalid="invalid"
                :focusOnHover="false"
                emptyMessage=" "
                scrollHeight="308px"
                class="selected-items"
                fluid
                :data-key="valueKey"
                :pt="listBoxPassThrough"
                :virtualScrollerOptions="virtualScrollerOptions"
                @item-dblclick="handleUnselectItemDoubleClick"
            >
                <template #option="{ option }">
                    <div
                        class="select-option"
                        :data-test-id="`${testId}-multiselect-selected-option-${option[valueKey]}`"
                    >
                        <span class="select-option-label">
                            {{ option[labelKey] }}
                            <slot
                                name="option-suffix"
                                :option="option"
                            />
                        </span>

                        <button
                            type="button"
                            class="option-add-button"
                            :data-test-id="`${testId}-multiselect-selected-option-${option[valueKey]}-remove-button`"
                            @click.prevent="unselectSingleItem(option)"
                        >
                            <img
                                src="/images/icon-close-black.svg"
                                alt="Select"
                            />
                        </button>
                    </div>
                </template>
            </Listbox>
        </div>
    </div>
</template>

<script>
import Listbox from 'primevue/listbox'

export default {
    components: { Listbox },
    props: {
        allItems: {
            type: Array,
            required: true,
        },
        selectedValues: {
            type: Array,
            required: true,
        },
        valueKey: {
            type: String,
            default: 'id',
        },
        labelKey: {
            type: String,
            default: 'name',
        },
        label: {
            type: String,
            default: 'Items',
        },
        invalid: {
            type: Boolean,
            default: false,
        },
        testId: {
            type: String,
            default: '',
        },
    },

    emits: ['items-selected', 'items-unselected'],

    data() {
        return {
            highlightedAvailableItems: [],
            highlightedSelectedItem: null,
            listBoxPassThrough: {
                // add filter textbox data-test-id
                pcFilter: {
                    root: {
                        'data-test-id': `${this.testId}-multiselect-filter-input`,
                    },
                },
                emptyMessage: {
                    style: {
                        fontStyle: 'italic',
                        fontWeight: 500,
                        color: '#141218',
                        opacity: 0.56,
                    },
                },
            },
        }
    },

    computed: {
        selectedSet() {
            return new Set(this.selectedValues)
        },
        availableItems() {
            return this.allItems.filter(({ id }) => !this.selectedSet.has(id))
        },
        selectedItems() {
            return this.allItems.filter(({ id }) => this.selectedSet.has(id))
        },
        virtualScrollerOptions() {
            return this.allItems.length > 500 ? { itemSize: 32 } : null
        },
    },

    watch: {
        selectedValues(newSelectedValues) {
            this.highlightedAvailableItems = this.highlightedAvailableItems.filter(
                (value) => !newSelectedValues.includes(value)
            )
        },
    },
    methods: {
        clearAvailableHighlight() {
            this.highlightedAvailableItems = []
        },
        emitSelected(items) {
            this.$emit('items-selected', items)
        },
        emitUnselected(items) {
            this.$emit('items-unselected', items)
        },
        unselectAll() {
            this.emitUnselected(this.selectedValues)
        },
        selectHighlightedItems() {
            this.emitSelected(this.highlightedAvailableItems)
            this.clearAvailableHighlight()
        },
        handleSelectItemDoubleClick({ value }) {
            this.emitSelected([value[this.valueKey]])
        },
        selectSingleItem(item) {
            this.emitSelected([item[this.valueKey]])
        },
        unselectSingleItem(item) {
            this.emitUnselected([item[this.valueKey]])
        },
        handleUnselectItemDoubleClick({ value }) {
            this.emitUnselected([value[this.valueKey]])
        },
    },
}
</script>
<style lang="scss">
.select-container {
    display: flex;
    width: 731px;

    .select-divider {
        font-size: 12px;
        width: 103px;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        gap: 0.4rem;
        text-align: center;
        line-height: 16px;

        button {
            cursor: pointer;
            background: none;
            border: none;
            padding: 0;
        }
    }

    .select-item {
        width: 314px;
        display: flex;
        flex-direction: column;
        gap: 0.7rem;

        .select-item-header {
            display: flex;
            flex-direction: row;
            justify-content: space-between;

            a {
                text-decoration: none;
                color: #59c966;
                font-weight: 900;
            }
        }
    }

    .p-listbox {
        /* force all boxes to match height */
        min-height: 310px;
    }

    &.p-listbox.p-invalid {
        border-color: #f96a62 !important; /* Your custom invalid color */
    }

    .available-items li.p-listbox-option.p-listbox-option-selected {
        /* override highlight color - to show add button */
        background-color: #211f26 !important;
        color: #fbfbfb !important;
    }

    .select-option {
        width: 100%;
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        align-items: center;
        height: 21px;

        .select-option-label {
            display: flex;
            align-items: center;
            gap: 8px;
            min-width: 0;

            /* keep slotted content (e.g. a type badge) from being squashed by long labels */
            > * {
                flex-shrink: 0;
            }
        }

        .option-add-button {
            cursor: pointer;
            background: none;
            border: none;
            padding: 0;
        }
    }
}
</style>
